import { useEffect, useRef, useState } from "react";
import { LocalizedLink as Link } from "./LocalizedLink";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  CreditCard, Users, Calendar, FileSignature, Zap, BarChart3, Sparkles,
  Workflow, Star, Puzzle, Image as ImageIcon, Menu, X, ChevronDown, ArrowRight,
  Brain, Wand2, UserPlus, QrCode, FormInput,
} from "lucide-react";
import logoSvg from "@/assets/banana-logo.svg";
import { CountryPicker } from "./CountryPicker";
import { CartButton } from "./CartDrawer";
import { useT } from "@/lib/i18n/dict";
import { useCurrency } from "@/lib/ecom/currency";
import { formatMoney, getProductBasePrice } from "@/lib/ecom/catalog";
import prodGold from "@/assets/products/custom-gold.webp";
import prodMetal from "@/assets/products/custom-black-metal.webp";
import prodBamboo from "@/assets/products/custom-bamboo.webp";
import prodWhite from "@/assets/products/custom-white-pvc.webp";
import prodBlack from "@/assets/products/custom-black-pvc.webp";
import prodGoogle from "@/assets/products/retail-google-review.webp";

type MenuGroup = {
  labelKey: string;
  hub: string;
  items: { titleKey: string; descKey: string; href: string; icon: typeof CreditCard }[];
};

const PLATFORM: MenuGroup[] = [
  {
    labelKey: "hdr.group.identity",
    hub: "/platform/identity-and-sharing",
    items: [
      { titleKey: "hdr.item.cards.t", descKey: "hdr.item.cards.d", href: "/platform/cards", icon: CreditCard },
      { titleKey: "hdr.item.nfc.t", descKey: "hdr.item.nfc.d", href: "/platform/nfc", icon: Sparkles },
      { titleKey: "hdr.item.sharing.t", descKey: "hdr.item.sharing.d", href: "/platform/sharing", icon: QrCode },
    ],
  },
  {
    labelKey: "hdr.group.ai",
    hub: "/platform/ai-suite",
    items: [
      { titleKey: "hdr.item.brain.t", descKey: "hdr.item.brain.d", href: "/platform/ai", icon: Brain },
      { titleKey: "hdr.item.enrich.t", descKey: "hdr.item.enrich.d", href: "/platform/ai-enrichment", icon: UserPlus },
      { titleKey: "hdr.item.aitools.t", descKey: "hdr.item.aitools.d", href: "/platform/ai-tools", icon: Wand2 },
    ],
  },
  {
    labelKey: "hdr.group.crm",
    hub: "/platform/crm",
    items: [
      { titleKey: "hdr.item.contacts.t", descKey: "hdr.item.contacts.d", href: "/platform/contacts", icon: Users },
      { titleKey: "hdr.item.pipelines.t", descKey: "hdr.item.pipelines.d", href: "/platform/pipelines", icon: Workflow },
      { titleKey: "hdr.item.analytics.t", descKey: "hdr.item.analytics.d", href: "/platform/analytics", icon: BarChart3 },
    ],
  },
  {
    labelKey: "hdr.group.run",
    hub: "/platform/run-your-business",
    items: [
      { titleKey: "hdr.item.bookings.t", descKey: "hdr.item.bookings.d", href: "/platform/bookings", icon: Calendar },
      { titleKey: "hdr.item.contracts.t", descKey: "hdr.item.contracts.d", href: "/platform/contracts", icon: FileSignature },
      { titleKey: "hdr.item.automations.t", descKey: "hdr.item.automations.d", href: "/platform/automations", icon: Zap },
      { titleKey: "hdr.item.reviews.t", descKey: "hdr.item.reviews.d", href: "/platform/reviews", icon: Star },
      { titleKey: "hdr.item.integrations.t", descKey: "hdr.item.integrations.d", href: "/platform/integrations", icon: Puzzle },
      { titleKey: "hdr.item.forms.t", descKey: "hdr.item.forms.d", href: "/platform/sharing", icon: FormInput },
    ],
  },
];

const SOLUTIONS = [
  { titleKey: "hdr.sol.sales.t", descKey: "hdr.sol.sales.d", href: "/solutions/sales" },
  { titleKey: "hdr.sol.founders.t", descKey: "hdr.sol.founders.d", href: "/solutions/founders" },
  { titleKey: "hdr.sol.realestate.t", descKey: "hdr.sol.realestate.d", href: "/solutions/real-estate" },
  { titleKey: "hdr.sol.enterprise.t", descKey: "hdr.sol.enterprise.d", href: "/solutions/enterprise" },
];

type Product = {
  titleKey: string;
  descKey: string;
  price: string;
  image: string;
  badgeKey?: string;
  href: string;
  slug?: string;
};

const PRODUCTS_CUSTOM: Product[] = [
  { titleKey: "hdr.prod.gold.t",   descKey: "hdr.prod.gold.d",   price: "From $108", image: prodGold,   badgeKey: "hdr.badge.luxury",     href: "/shop/24kt-gold", slug: "24kt-gold" },
  { titleKey: "hdr.prod.metal.t",  descKey: "hdr.prod.metal.d",  price: "From $72",  image: prodMetal,  badgeKey: "hdr.badge.premium",    href: "/shop/metal",     slug: "metal" },
  { titleKey: "hdr.prod.bamboo.t", descKey: "hdr.prod.bamboo.d", price: "From $60",  image: prodBamboo, badgeKey: "hdr.badge.eco",        href: "/shop/bamboo",    slug: "bamboo" },
  { titleKey: "hdr.prod.white.t",  descKey: "hdr.prod.white.d",  price: "From $54",  image: prodWhite,                                    href: "/shop/white-pvc", slug: "white-pvc" },
  { titleKey: "hdr.prod.black.t",  descKey: "hdr.prod.black.d",  price: "From $54",  image: prodBlack,  badgeKey: "hdr.badge.bestseller", href: "/shop/black-pvc", slug: "black-pvc" },
  { titleKey: "hdr.prod.google.t", descKey: "hdr.prod.google.d", price: "From $30",  image: prodGoogle,                                   href: "/shop/google-review", slug: "google-review" },
];

const PRODUCTS_RETAIL: Product[] = [];

export function Header() {
  const t = useT();
  const { country } = useCurrency();
  const priceFor = (p: Product) => {
    if (!p.slug) return p.price;
    return `${t("hdr.from")} ${formatMoney(getProductBasePrice(p.slug, country.code), country)}`;
  };
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const scrollFrame = useRef<number | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 140);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openWith = (name: string) => {
    cancelClose();
    setOpenMenu(name);
  };

  useEffect(() => {
    const updateHeader = () => {
      const y = Math.max(window.scrollY, 0);
      const delta = y - lastScrollY.current;

      setScrolled(y > 4);

      if (y <= 8) {
        setHidden(false);
      } else if (delta > 6 && y > 80) {
        setHidden(true);
        setOpenMenu(null);
      } else if (delta < -1) {
        setHidden(false);
      }

      lastScrollY.current = y;
      scrollFrame.current = null;
    };

    const onScroll = () => {
      if (scrollFrame.current !== null) return;
      scrollFrame.current = window.requestAnimationFrame(updateHeader);
    };

    lastScrollY.current = Math.max(window.scrollY, 0);
    updateHeader();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollFrame.current !== null) {
        window.cancelAnimationFrame(scrollFrame.current);
      }
    };
  }, []);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);
  const ALL_PRODUCTS: Product[] = [...PRODUCTS_CUSTOM, ...PRODUCTS_RETAIL];
  const MOBILE_PRODUCTS = ALL_PRODUCTS.slice(0, 4);

  return (
    <>
    <header
      className={`sticky top-0 z-50 w-full border-b transition-[background-color,border-color,transform] duration-300 will-change-transform ${
        scrolled ? "border-foreground/10 bg-background/85 backdrop-blur" : "border-transparent bg-background/0"
      } translate-y-0`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="banana.cards">
          <img src={logoSvg} alt="banana.cards" width={115} height={20} className="h-5 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="ml-6 hidden flex-1 items-center gap-1 lg:flex">
          <button
            type="button"
            onMouseEnter={() => openWith("products")}
            onMouseLeave={scheduleClose}
            onClick={() => setOpenMenu((m) => (m === "products" ? null : "products"))}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold transition hover:bg-muted ${
              openMenu === "products" ? "bg-muted" : ""
            }`}
            aria-expanded={openMenu === "products"}
          >
            {t("nav.products")}
            <ChevronDown className={`h-3.5 w-3.5 transition ${openMenu === "products" ? "rotate-180" : ""}`} />
          </button>
          <button
            type="button"
            onMouseEnter={() => openWith("platform")}
            onMouseLeave={scheduleClose}
            onClick={() => setOpenMenu((m) => (m === "platform" ? null : "platform"))}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold transition hover:bg-muted ${
              openMenu === "platform" ? "bg-muted" : ""
            }`}
            aria-expanded={openMenu === "platform"}
          >
            {t("nav.platform")}
            <ChevronDown className={`h-3.5 w-3.5 transition ${openMenu === "platform" ? "rotate-180" : ""}`} />
          </button>
          <Link to="/teams" onMouseEnter={scheduleClose} className="rounded-full px-3 py-1.5 text-sm font-semibold transition hover:bg-muted">
            {t("nav.teams")}
          </Link>
          <Link to="/pricing" onMouseEnter={scheduleClose} className="rounded-full px-3 py-1.5 text-sm font-semibold transition hover:bg-muted">
            {t("nav.pricing")}
          </Link>

          {/* Mega menus */}
          {openMenu === "products" && (
            <div
              className="absolute left-1/2 top-full w-[min(1180px,95vw)] -translate-x-1/2 px-4 pt-3"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <div className="overflow-hidden rounded-2xl border border-foreground/10 bg-card shadow-xl shadow-foreground/5">
                <div className="grid grid-cols-12">
                  <div className="col-span-9 p-5">
                  <div className="mb-3.5 flex items-end justify-between px-1">
                    <div>
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{t("nav.allProducts")}</p>
                      <p className="mt-0.5 text-[12px] text-muted-foreground">{t("nav.allProductsDesc")}</p>
                    </div>
                    <Link to="/shop" onClick={() => setOpenMenu(null)} className="text-[12px] font-bold underline-offset-4 hover:underline">
                      {t("nav.shopAll")}
                    </Link>
                  </div>
                  <div className="grid grid-cols-3 gap-2.5">
                    {PRODUCTS_CUSTOM.map((p) => (
                      <Link
                        key={p.href}
                        to={p.href}
                        onClick={() => setOpenMenu(null)}
                        className="group relative overflow-hidden rounded-lg border border-foreground/10 bg-card transition"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                          <img
                            src={p.image}
                            alt={t(p.titleKey as any)}
                            loading="lazy"
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                          />
                          {p.badgeKey && (
                            <span className="absolute left-2 top-2 inline-flex items-center rounded-full bg-background/90 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-foreground backdrop-blur">
                              {t(p.badgeKey as any)}
                            </span>
                          )}
                        </div>
                        <div className="p-2.5">
                          <div className="flex items-start justify-between gap-1.5">
                            <span className="block text-[12px] font-semibold leading-tight">{t(p.titleKey as any)}</span>
                            <span className="shrink-0 text-[11px] font-bold">{priceFor(p)}</span>
                          </div>
                          <p className="mt-1 line-clamp-1 text-[11px] leading-snug text-muted-foreground">{t(p.descKey as any)}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                  </div>
                  <div className="relative col-span-3 overflow-hidden border-l border-foreground/10 bg-brand-dark p-7 text-background">
                    <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-60 blur-3xl" style={{ background: "radial-gradient(circle, oklch(0.96 0.21 117 / 0.55), transparent 70%)" }} aria-hidden />
                    <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full opacity-50 blur-3xl" style={{ background: "radial-gradient(circle, oklch(0.72 0.18 295 / 0.55), transparent 70%)" }} aria-hidden />
                    <div className="relative">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-lime/15 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider text-brand-lime">
                        {t("nav.forTeams")}
                      </span>
                      <p className="mt-5 text-lg font-semibold leading-snug">
                        {t("nav.teamsHeadline")}
                      </p>
                      <p className="mt-2 text-[12.5px] leading-relaxed text-background/65">
                        {t("nav.teamsBody")}
                      </p>
                      <Link
                        to="/teams"
                        onClick={() => setOpenMenu(null)}
                        className="mt-6 inline-flex items-center gap-1 rounded-full bg-brand-lime px-3.5 py-2 text-[12px] font-bold text-brand-lime-foreground"
                      >
                        {t("nav.talkToSales")}
                      </Link>
                      <div className="mt-8 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-brand-lime">{t("nav.bulkPricing")}</p>
                        <p className="mt-2 text-[13px] font-semibold leading-snug">{t("nav.bulkBody")}</p>
                        <Link to="/teams" onClick={() => setOpenMenu(null)} className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-brand-lime underline-offset-4 hover:underline">
                          {t("nav.getQuote")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-foreground/10 bg-muted/40 px-7 py-3">
                  <p className="text-[12px] text-muted-foreground">{t("nav.brandedQ")}</p>
                  <Link to="/teams" onClick={() => setOpenMenu(null)} className="text-[12px] font-bold underline-offset-4 hover:underline">
                    {t("nav.talkToSales")}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {openMenu === "platform" && (
            <div
              className="absolute left-1/2 top-full w-[min(1240px,95vw)] -translate-x-1/2 px-4 pt-3"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <div className="overflow-hidden rounded-2xl border border-foreground/10 bg-card shadow-xl shadow-foreground/5">
                <div className="grid grid-cols-12">
                  <div className="col-span-9 p-7">
                    {/* Top row: 3 grouped link columns */}
                    <div className="grid grid-cols-3 gap-x-2 gap-y-6">
                      {PLATFORM.filter((g) => g.labelKey !== "hdr.group.run").map((group) => (
                      <div key={group.labelKey}>
                        <Link
                          to={group.hub}
                          onClick={() => setOpenMenu(null)}
                          className="mb-3 inline-flex items-center gap-1 px-3 text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground transition hover:text-foreground"
                        >
                          {t(group.labelKey as any)}
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                        <div className="flex flex-col gap-0.5">
                          {group.items.map((it) => {
                            const Icon = it.icon;
                            return (
                              <Link
                                key={it.href}
                                to={it.href}
                                onClick={() => setOpenMenu(null)}
                                className="group flex items-start gap-3 rounded-lg px-3 py-2.5 transition hover:bg-muted/60"
                              >
                                <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-foreground/70 transition group-hover:text-foreground" strokeWidth={1.75} />
                                <span className="min-w-0">
                                  <span className="block text-[13.5px] font-semibold leading-tight">{t(it.titleKey as any)}</span>
                                  <span className="mt-1 block text-[12px] leading-snug text-muted-foreground">{t(it.descKey as any)}</span>
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                      ))}
                    </div>

                    {/* Bottom row: Run your business as a 3x2 grid of gradient cards */}
                    {(() => {
                      const run = PLATFORM.find((g) => g.labelKey === "hdr.group.run");
                      if (!run) return null;
                      const tints = [
                        "from-brand-lime/20 via-card to-card",
                        "from-foreground/[0.06] via-card to-card",
                        "from-brand-lime/15 via-card to-card",
                        "from-foreground/[0.05] via-card to-card",
                        "from-brand-lime/10 via-card to-card",
                        "from-foreground/[0.07] via-card to-card",
                      ];
                      return (
                        <div className="mt-7 border-t border-foreground/10 pt-6">
                          <Link
                            to={run.hub}
                            onClick={() => setOpenMenu(null)}
                            className="mb-4 inline-flex items-center gap-1 px-1 text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground transition hover:text-foreground"
                          >
                            {t(run.labelKey as any)}
                            <ArrowRight className="h-3 w-3" />
                          </Link>
                          <div className="grid grid-cols-3 gap-2.5">
                            {run.items.map((it, idx) => {
                              const Icon = it.icon;
                              return (
                                <Link
                                  key={it.href}
                                  to={it.href}
                                  onClick={() => setOpenMenu(null)}
                                  className={`group relative overflow-hidden rounded-xl border border-foreground/10 bg-gradient-to-br ${tints[idx % tints.length]} p-3.5 transition`}
                                >
                                  <div className="flex items-start gap-3">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-card ring-1 ring-foreground/10">
                                      <Icon className="h-[18px] w-[18px] text-foreground/80 transition group-hover:text-foreground" strokeWidth={1.75} />
                                    </span>
                                    <span className="min-w-0">
                                      <span className="block text-[13.5px] font-semibold leading-tight">{t(it.titleKey as any)}</span>
                                      <span className="mt-1 block text-[12px] leading-snug text-muted-foreground">{t(it.descKey as any)}</span>
                                    </span>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="relative col-span-3 overflow-hidden border-l border-foreground/10 bg-brand-dark p-7 text-background">
                    <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-60 blur-3xl" style={{ background: "radial-gradient(circle, oklch(0.96 0.21 117 / 0.55), transparent 70%)" }} aria-hidden />
                    <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full opacity-50 blur-3xl" style={{ background: "radial-gradient(circle, oklch(0.72 0.18 295 / 0.55), transparent 70%)" }} aria-hidden />
                    <div className="relative">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-lime/15 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider text-brand-lime">
                      {t("nav.whatsNew")}
                    </span>
                    <p className="mt-5 text-lg font-semibold leading-snug">
                      {t("nav.brainNew")}
                    </p>
                    <p className="mt-2 text-[12.5px] leading-relaxed text-background/65">
                      {t("nav.brainBody")}
                    </p>
                    <Link
                      to="/platform/ai"
                      onClick={() => setOpenMenu(null)}
                      className="mt-6 inline-flex items-center gap-1 text-[12.5px] font-bold text-brand-lime underline-offset-4 hover:underline"
                    >
                      {t("nav.readAnnouncement")}
                    </Link>
                    <div className="mt-8 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-brand-lime">{t("nav.tryFree")}</p>
                      <p className="mt-2 text-[13px] font-semibold leading-snug">{t("nav.tryFreeBody")}</p>
                      <CtaLink to="/signup" onClick={() => setOpenMenu(null)} className="mt-3 inline-flex items-center gap-1 rounded-full bg-brand-lime px-3 py-1.5 text-[11px] font-bold text-brand-lime-foreground">
                        {t("nav.getStartedArrow")}
                      </CtaLink>
                    </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-foreground/10 bg-muted/40 px-7 py-3">
                  <p className="text-[12px] text-muted-foreground">
                    {t("nav.overviewQ")}
                  </p>
                  <Link
                    to="/pricing"
                    onClick={() => setOpenMenu(null)}
                    className="text-[12px] font-bold underline-offset-4 hover:underline"
                  >
                    {t("nav.seePlans")}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {openMenu === "solutions" && (
            <div
              className="absolute left-1/2 top-full w-[min(840px,95vw)] -translate-x-1/2 px-4 pt-3"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <div className="overflow-hidden rounded-2xl border border-foreground/10 bg-card shadow-xl shadow-foreground/5">
                <div className="grid grid-cols-12">
                  <div className="col-span-8 grid grid-cols-2 gap-0.5 p-5">
                    {SOLUTIONS.map((it) => (
                      <Link
                        key={it.href}
                        to={it.href}
                        onClick={() => setOpenMenu(null)}
                        className="rounded-lg border border-foreground/10 px-3 py-3 transition hover:bg-muted/60"
                      >
                        <span className="block text-[13.5px] font-semibold leading-tight">{t(it.titleKey as any)}</span>
                        <span className="mt-1 block text-[12px] leading-snug text-muted-foreground">{t(it.descKey as any)}</span>
                      </Link>
                    ))}
                  </div>
                  <div className="col-span-4 border-l border-foreground/10 bg-muted p-6">
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                      {t("nav.customerStory")}
                    </p>
                    <p className="mt-4 font-display text-2xl leading-tight">
                      65× <em className="italic">faster</em> follow-up at Northbeam.
                    </p>
                    <Link
                      to="/about"
                      onClick={() => setOpenMenu(null)}
                      className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-bold underline-offset-4 hover:underline"
                    >
                      {t("nav.readStory")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

        </nav>

        <div className="ml-auto flex items-center gap-2">
          <CountryPicker />
          <CartButton />
          <a
            href="https://app.banana.cards"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-full px-3 py-1.5 text-sm font-semibold transition hover:bg-muted md:inline-flex"
          >
            {t("nav.signIn")}
          </a>
          <a
            href="https://app.banana.cards"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center rounded-xl bg-foreground px-4 py-2 text-xs font-bold text-background transition hover:opacity-90 md:inline-flex md:text-sm"
          >
            {t("nav.getStarted")}
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="rounded-full p-2 hover:bg-muted lg:hidden"
            aria-label={t("nav.openMenu")}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </header>

      {/* Mobile nav — rendered outside <header> so its `position: fixed` is
          relative to the viewport, not the transformed header element. */}
      {mobileOpen && (
        <div className="fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto bg-background lg:hidden">
          <div className="mx-auto max-w-7xl px-4 pb-28 pt-4">
            {/* Featured products grid — mobile megamenu */}
            <div className="mb-5">
              <div className="mb-3 flex items-end justify-between">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{t("nav.shopProducts")}</p>
                <Link to="/shop" onClick={closeMobile} className="text-[12px] font-bold underline-offset-4 hover:underline">
                  {t("nav.shopAll")}
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {MOBILE_PRODUCTS.map((p) => (
                  <Link
                    key={p.href}
                    to={p.href}
                    onClick={closeMobile}
                    className="group overflow-hidden rounded-xl border border-foreground/5 bg-card transition active:scale-[0.98]"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                      <img src={p.image} alt={t(p.titleKey as any)} loading="lazy" className="h-full w-full object-cover" />
                      {p.badgeKey && (
                        <span className="absolute left-2 top-2 inline-flex items-center rounded-full bg-background/90 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-foreground backdrop-blur">
                          {t(p.badgeKey as any)}
                        </span>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-[12px] font-semibold leading-tight">{t(p.titleKey as any)}</p>
                      <p className="mt-1 text-[11px] font-bold text-muted-foreground">{priceFor(p)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Quick links */}
            <div className="mb-2 grid grid-cols-2 gap-2">
              <Link to="/teams" onClick={closeMobile} className="flex items-center justify-between rounded-xl border border-foreground/5 bg-card px-4 py-3 text-sm font-semibold transition active:scale-[0.98]">
                {t("nav.teams")} <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              <Link to="/pricing" onClick={closeMobile} className="flex items-center justify-between rounded-xl border border-foreground/5 bg-card px-4 py-3 text-sm font-semibold transition active:scale-[0.98]">
                {t("nav.pricing")} <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              <Link to="/shop" onClick={closeMobile} className="flex items-center justify-between rounded-xl border border-foreground/5 bg-card px-4 py-3 text-sm font-semibold transition active:scale-[0.98]">
                {t("nav.nfcShop")} <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              <Link to="/about" onClick={closeMobile} className="flex items-center justify-between rounded-xl border border-foreground/5 bg-card px-4 py-3 text-sm font-semibold transition active:scale-[0.98]">
                {t("nav.about")} <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            </div>

            {/* Platform accordion — grouped by category */}
            <div className="mt-3 overflow-hidden rounded-xl border border-foreground/5 bg-card">
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3.5 text-sm font-bold">
                  {t("nav.platform")}
                  <ChevronDown className="h-4 w-4 transition group-open:rotate-180" />
                </summary>
                <div className="space-y-4 border-t border-foreground/10 px-4 py-4">
                  {PLATFORM.map((group) => (
                    <div key={group.labelKey}>
                      <Link
                        to={group.hub}
                        onClick={closeMobile}
                        className="mb-2 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground"
                      >
                        {t(group.labelKey as any)}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                      <div className="grid gap-0.5">
                        {group.items.map((it) => {
                          const Icon = it.icon;
                          return (
                            <Link
                              key={it.href}
                              to={it.href}
                              onClick={closeMobile}
                              className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition active:bg-muted"
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                                <Icon className="h-[16px] w-[16px] text-foreground/75" strokeWidth={1.75} />
                              </span>
                              <span className="text-[13.5px] font-semibold">{t(it.titleKey as any)}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            </div>

            {/* Solutions accordion */}
            <div className="mt-2 overflow-hidden rounded-xl border border-foreground/5 bg-card">
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3.5 text-sm font-bold">
                  {t("nav.solutions")}
                  <ChevronDown className="h-4 w-4 transition group-open:rotate-180" />
                </summary>
                <div className="grid gap-0.5 border-t border-foreground/10 px-2 py-2">
                  {SOLUTIONS.map((it) => (
                    <Link key={it.href} to={it.href} onClick={closeMobile} className="rounded-lg px-3 py-2.5 text-[13.5px] font-semibold transition active:bg-muted">
                      <span className="block leading-tight">{t(it.titleKey as any)}</span>
                      <span className="mt-0.5 block text-[12px] font-normal leading-snug text-muted-foreground">{t(it.descKey as any)}</span>
                    </Link>
                  ))}
                </div>
              </details>
            </div>

            {/* What's new promo */}
            <Link
              to="/platform/ai"
              onClick={closeMobile}
              className="relative mt-4 block overflow-hidden rounded-xl border border-white/10 bg-brand-dark p-5 text-background"
            >
              <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-60 blur-3xl" style={{ background: "radial-gradient(circle, oklch(0.96 0.21 117 / 0.55), transparent 70%)" }} aria-hidden />
              <span className="relative inline-flex items-center gap-1.5 rounded-full bg-brand-lime/15 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider text-brand-lime">
                {t("nav.whatsNew")}
              </span>
              <p className="relative mt-3 text-[15px] font-semibold leading-snug">
                {t("nav.brainNew")}
              </p>
              <span className="relative mt-3 inline-flex items-center gap-1 text-[12.5px] font-bold text-brand-lime">
                {t("nav.readAnnouncementMobile")} <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>

            {/* Sticky bottom CTAs */}
            <div className="fixed inset-x-0 bottom-0 z-10 border-t border-foreground/10 bg-background/95 px-4 py-3 backdrop-blur">
              <div className="mx-auto flex max-w-7xl items-center gap-2">
                <a href="https://app.banana.cards" target="_blank" rel="noopener noreferrer" onClick={closeMobile} className="flex flex-1 items-center justify-center rounded-full border border-foreground/15 bg-card px-4 py-2.5 text-sm font-bold transition active:scale-[0.98]">
                  {t("nav.signIn")}
                </a>
                <a href="https://app.banana.cards" target="_blank" rel="noopener noreferrer" onClick={closeMobile} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-foreground px-4 py-2.5 text-sm font-bold text-background transition active:scale-[0.98]">
                  {t("nav.getStarted")} <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
